import React, { useCallback } from "react";
import { Button, Card, Flex, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Column } from "@jsq/ds/layouts";
import { Colors } from "@jsq/ds/styles";
import { CurrencyCode } from "#graphql/supergraphEnums";
import { useCreateCanopyPaymentBatchMutation } from "#graphql/supergraphHooks";
import { clientContext } from "#utils/graphql_utils";
import Money from "../../../Money";
import { addMoney, DEFAULT_PRECISION } from "../../../Wallet/utils";
import { useDistributionWorkflowContext } from "../../Context/context";
import { MissingDistributionBatchIdError } from "../../errors";
import { DistributionWorkflowStepProps } from "../../types";
import { ReviewRow } from "./ReviewAndSubmitRow";
import {
  actionButtonsContainer,
  infoCard,
  infoCardText,
  paymentBatchDetailsSubHeader,
} from "./styles_vanilla.css";
import { buildCreatePaymentBatchInput } from "./utils";

export const ReviewAndSubmitStep: React.FC<DistributionWorkflowStepProps> = ({
  onBack,
  onContinue,
}: DistributionWorkflowStepProps) => {
  const {
    clientName,
    investmentEntityName,
    distributionBatchId,
    paymentDescription,
    scheduledPaymentProcessDate,
    selectedPositions,
    numberOfAvailablePositions,
    makerNote,
  } = useDistributionWorkflowContext();
  const [createPaymentBatch, { loading: isSubmitting }] = useCreateCanopyPaymentBatchMutation({
    context: clientContext(),
  });

  const formattedDate = scheduledPaymentProcessDate?.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  const zeroAmount = {
    amount: 0,
    currency: selectedPositions[0]?.netPayment.currency || CurrencyCode.Usd,
    precision: DEFAULT_PRECISION,
  };

  const paymentTotal = selectedPositions.reduce(
    (acc, position) => addMoney(acc, [position.netPayment]),
    [zeroAmount],
  );

  const paymentTotalText = `Payment total (${selectedPositions.length} positions)`;
  const paymentTotalElement = <Money money={paymentTotal[0]} rounded={false} />;

  const onSubmit = useCallback(() => {
    if (!distributionBatchId) {
      // unreachable as we should have a distribution batch id at this point.
      throw new MissingDistributionBatchIdError();
    }

    const createPaymentBatchInput = buildCreatePaymentBatchInput({
      selectedPositions,
      paymentDescription,
      distributionBatchId,
      scheduledPaymentProcessDate,
      makerNote,
    });

    createPaymentBatch({
      variables: { createPaymentBatchInput },
    })
      .then(() => {
        onContinue();
      })
      .catch((err: Error) => {
        onContinue(err);
      });
  }, [
    createPaymentBatch,
    distributionBatchId,
    selectedPositions,
    paymentDescription,
    scheduledPaymentProcessDate,
    onContinue,
  ]);

  return (
    <Column size="md">
      <Card title="Review and submit for review">
        <Flex flexDirection="column" gap={16}>
          <Text variant="h4">Client details</Text>
          <ReviewRow keyText={"Client"} valueText={clientName} />
          <ReviewRow keyText={"Investment entity"} valueText={investmentEntityName} />
          <Text variant="h4" className={paymentBatchDetailsSubHeader}>
            Payment batch details
          </Text>
          <ReviewRow keyText={"Payment date"} valueText={formattedDate} />
          <ReviewRow
            keyText={"Payment description for all recipients"}
            valueText={paymentDescription}
          />
          <ReviewRow keyText={paymentTotalText} valueText={paymentTotalElement} />
          {numberOfAvailablePositions > selectedPositions.length && (
            <Card className={infoCard}>
              <Flex alignItems="center">
                <Icon color={Colors.Informative700} type="info-circle" size="medium"></Icon>
                <Text className={infoCardText} variant="secondary-md">
                  {numberOfAvailablePositions - selectedPositions.length} position(s) were
                  available to pay, but excluded
                </Text>
              </Flex>
            </Card>
          )}
          {makerNote && <ReviewRow keyText={"Notes for reviewer"} valueText={makerNote} />}

          <Flex flexDirection="row-reverse" className={actionButtonsContainer} gap={10}>
            <Button
              variant={"primary"}
              onClick={onSubmit}
              isLoading={isSubmitting}
              data-testid="submit-button"
            >
              Submit for review
            </Button>
            <Button
              disabled={isSubmitting}
              variant={"neutral"}
              onClick={onBack}
              data-testid="back-button"
            >
              Back
            </Button>
          </Flex>
        </Flex>
      </Card>
    </Column>
  );
};
